import { call, all, put, takeLatest, select, fork } from 'redux-saga/effects';
import qs from 'query-string';
import { get } from 'lodash';
import EventNames from 'src/lib/eventNames';
import appDux from 'src/redux/app';
import { initNodeStylesSaga } from 'src/redux/app/sagas';
import utils from 'src/utils';
import jobDesignsDux from 'src/redux/jobDesigns';
import { generateJobDesignsSaga } from 'src/redux/jobDesigns/sagas';
import macheteDux from 'src/redux/machete';
import {
  textureModelSaga,
  setTexturesV2,
  loadModelSaga,
} from 'src/redux/machete/sagas';
import trackingDux from 'src/redux/tracking';
import GraphQLApi from 'src/apis/GraphQLApi';

import userSagas from 'src/redux/user/sagas';
import userDux from 'src/redux/user';
import keyboardEventsDux from 'src/redux/keyboardEvents';
import uiDux from 'src/redux/ui/';
import constants from 'src/constants';
import ViewerApi from 'src/apis/viewer';
import splitIO from 'src/services/splitIOService';
import initializeProductCatalogDataSaga from './initializeProductCatalogDataSaga';

const { SHARE_TOKEN_PARAM } = constants.jobShare;
const { isFromNativeApp } = utils.app;

export function* initFeatureFlagsSaga() {
  const commonMetricData = yield select(
    trackingDux.selectors.getCommonMetricData,
  );
  const userKey = commonMetricData?.user_id || 'anonymous';
  splitIO.init(userKey, commonMetricData);
}

export function* fetchJobAndSavedDesignsSaga({ jobId, modelId, shareToken }) {
  const [
    { data: getJobDesignsResponse },
    {
      data: { job },
    },
  ] = yield all([
    call([ViewerApi, ViewerApi.getJobDesigns], jobId, shareToken),
    call([ViewerApi, ViewerApi.getJobDetails], jobId, shareToken),
  ]);

  // With multi-structure support, we pass model id and job id to the viewer.
  // The assumption is that old links will continue to work since they only have
  // a single model on the job and they don't have the model id in the url.
  // And if a pre multi-structure link is used, we just load the first model and
  // ignore the model id in the url because they only have one model.
  const isMultiStructure = job.models.length > 1 && modelId;
  const model = isMultiStructure
    ? job.models.find((m) => String(m.id) === modelId)
    : job.models[0];
  const savedDesigns = isMultiStructure
    ? getJobDesignsResponse.filter((design) => design.name.includes(modelId))
    : getJobDesignsResponse;

  yield put(appDux.actions.setJobDetails(job));
  yield put(macheteDux.actions.receiveHyperionModelData(model));

  return { job, model, savedDesigns };
}

export function* initDesignProSaga({
  payload: { jobId, modelId, queryString },
}) {
  const startInitTime = performance.now();
  const queryStringMap = qs.parse(queryString);
  const shareToken = queryStringMap[SHARE_TOKEN_PARAM];

  if (!shareToken) yield call(userSagas.checkAuthSaga);

  yield call(initFeatureFlagsSaga);
  yield put(trackingDux.actions.identify());

  if (shareToken) {
    yield put(appDux.actions.setShareToken(shareToken));
  }
  const { job, model, savedDesigns } = yield call(fetchJobAndSavedDesignsSaga, {
    jobId,
    modelId,
    shareToken,
  });

  yield fork(initializeProductCatalogDataSaga, { job });
  yield fork(initNodeStylesSaga);
  yield fork(textureModelSaga);
  yield fork(setTexturesV2);

  const user = yield select(userDux.selectors.getUserProfile);
  const shouldShowMeasureMode = !user?.isHomeowner && !shareToken;

  // when we are in the native apps we don't want to show the
  // footer because they have a native footer
  const isNativeApp = isFromNativeApp(queryStringMap);

  const jobDesigns = yield call(generateJobDesignsSaga, {
    savedDesigns,
    jobId,
    model,
  });
  const firstDesignToLoad = jobDesigns.saved.length
    ? jobDesigns.saved[0]
    : jobDesigns.original;
  const shouldFocus = true;

  yield put(jobDesignsDux.actions.updateSelectedDesignId(firstDesignToLoad.id));
  yield put(jobDesignsDux.actions.receiveJobDesigns(jobDesigns));
  yield call(
    loadModelSaga,
    { modelData: firstDesignToLoad, shouldAutoFocus: false },
    shouldFocus,
  );

  yield put(uiDux.actions.setIsNativeApp(isNativeApp));
  yield put(uiDux.actions.setShouldShowMeasureMode(shouldShowMeasureMode));

  const finishInitTime = performance.now();
  yield put(appDux.actions.setDesignProInitComplete());
  yield put(keyboardEventsDux.actions.initializeKeyboardEvents());
  yield put(macheteDux.actions.initializeMacheteEvents());
  yield put(trackingDux.actions.track(EventNames.viewer.VIEWER_LOADED));
  yield put(
    trackingDux.actions.track(EventNames.viewer.INITIAL_LOAD_TIME, {
      load_duration: finishInitTime - startInitTime,
    }),
  );

  const isRenderingsEnabled = yield select(uiDux.selectors.isRenderingsEnabled);

  if (isRenderingsEnabled) {
    const renderings = yield call(
      [GraphQLApi, GraphQLApi.getRenderings],
      JSON.parse(jobId),
    );

    yield put(appDux.actions.receiveRenderings(renderings));
  }
}

export default function* sagas() {
  yield all([takeLatest(macheteDux.actions.init, initDesignProSaga)]);
}
